import { Bridge } from '@/bridges/Bridge.js';

import { OnionRpcChannel } from '@/helpers/OnionRpc/OnionRpcChannel';
import {
  onionRpcGetMethodById,
  onionRpcResponseToHTML,
  onionRpcRequestToHTML,
  onionRpcResponseToObj,
  onionRpcRequestToObj,
} from '@/helpers/OnionRpc/OnionRpcMethods';

export class OnionBridge extends Bridge {
  constructor(options) {
    super();
    this.options = options;
    this.onionBootupTime = options.onionBootupTime;
    this.channel = new OnionRpcChannel('Onion-RPC', (txBuffer) => {
      // console.log('OnionRpc send: ', txBuffer.toString('hex'));
      for (let i = 0; i < txBuffer.length; i += 1) {
        this.emit('uartDidTransmit', 0, txBuffer[i]);
      }
    }, {
      autoStart: false,
    });
    this.channel.on('onionRpcMessage', this.onOnionRpcMessage.bind(this));
    this.channel.on('onionRpcUnexpectedMessage', this.onOnionRpcUnexpectedMessage.bind(this));
    this.bootupStartTime = null;
    this.bootupTimeLeft = 0;
    this.consumers = [];
    this.rpcVersionInfo = null;
    this.startupTimeout = null;
    this.waitForMessageHelper = null; // {resolve(response), reject, waitFor: MethodNumber}
    this.rpcMethodHtmlState = {}; // do not modify in this bridge,is for internal purposes of OnionRpcMethod
    this.rpcMethodObjState = {}; // do not modify in this bridge,is for internal purposes of OnionRpcMethod
    this.pendingWaitForEvent = [];
  }

  convertPayloadToString(result) {
    let resultAsString;
    if (!result) {
      resultAsString = "";
    } else if (!Array.isArray(result)) {
      resultAsString = result.toString();
    } else {
      resultAsString = result.map(value => {
        if (Buffer.isBuffer(value)) {
          const bufferStr = value.toString('hex');
          return bufferStr.length > 128 ? (`${bufferStr.substring(0, 128)}...`) : bufferStr;
        } else {
          return value != null ? value.toString() : 'null';
        }
      }).toString();
    }

    return resultAsString;
  }

  processRpcMessage(data) {
    const { activeRequest, result } = data;
    const { method } = activeRequest;
    let processingDone = false;
    const rpcMehtod = onionRpcGetMethodById(method);
    if (!rpcMehtod) {
      // TODO(robin) Error message
    } if (rpcMehtod.name === 'ReadEventBuffer') {
      // nop
    } else if (rpcMehtod.name === 'GetRpcVersion') {
      if (result && Array.isArray(result) && result.length === 4) {
        const rpcVersion = result[0];
        const rpcVersionString = [
          `${((rpcVersion & 0xFF0000) >> 16).toFixed(0).padStart(2, '0')}`,
          `${((rpcVersion & 0x00FF00) >> 8).toFixed(0).padStart(2, '0')}`,
          `${(rpcVersion & 0x0000FF).toFixed(0).padStart(2, '0')}`,
        ].join('.');
        this.rpcVersionInfo = {
          rpcVersion,
          rpcVersionString,
          changeset: result[1],
          customizationId: result[2],
          applicationSpecificId: result[3],
        };
      } else {
        global.console.log(`Unexpected rpc version message ${JSON.stringify(result)}`);
      }
    }
    return processingDone;
  }

  onOnionRpcMessage(data) {
    const processingDone = this.processRpcMessage(data);
    let request = null;
    if (!processingDone) {
      const resultAsString = this.convertPayloadToString(data.result);
      if (data.activeRequest) {
        const { activeRequest } = data;
        request = {
          activatedAt: activeRequest.activatedAt,
          requestAsString: this.convertPayloadToString(activeRequest.params),
          params: activeRequest.params,
          activeRequest,
        };
      }

      const message = {
        timestamp: Date.now(),
        expected: true,
        resultAsString,
        ...request,
        ...data,
      };

      if (message.method == null) {
        if (message.activeRequest && message.activeRequest.method != null) {
          message.method = message.activeRequest.method;
        } else {
          message.method = ' ';
        }
      }
      if (message.expected) {
        message.expectedAsString = 'Ja';
      } else {
        message.expectedAsString = 'Nein';
      }
      if (message.error != null) {
        message.errorAsString = message.error;
      } else {
        message.errorAsString = ' ';
      }

      if (message.method != null) {
        message.methodString = onionRpcGetMethodById(message.method)?.name;
      }

      if (message.method != null && message.params != null)  {
        try {
          message.requestHtmlString = onionRpcRequestToHTML(message.method, message.params, this.rpcMethodHtmlState);
        } catch(e) {
          global.console.log(e);
          global.console.log(message);
          message.requestHtmlString = `Fehler bei der Verarbeitung: ${e.message}`;
          // TODO(robin): implement error panel
        }
        try {
          message.requestObj = onionRpcRequestToObj(message.method, message.params, this.rpcMethodObjState);
        } catch(e) {
          global.console.log(e);
          global.console.log(message);
        }
      }

      if (!message.error) {
        try {
          message.resultHtmlString = onionRpcResponseToHTML(message.method, data.result, this.rpcMethodHtmlState);
        } catch(e) {
          global.console.log(e);
          global.console.log(message);
          message.resultHtmlString = `<ul><li><b>Fehler</b> bei der Verarbeitung: ${e.message}</li></ul>`;
          // TODO(robin): implement error panel
        }
        try {
          message.resultObj = onionRpcResponseToObj(message.method, data.result, this.rpcMethodObjState);
          if (this.pendingWaitForEvent.length > 0 && message.methodString === 'ReadEventBuffer') {
            for (let i = 0; i < this.pendingWaitForEvent.length; i += 1) {
              const pendingWaitForEvent = this.pendingWaitForEvent[i];
              let foundRequestedEvent = false;
              message.resultObj.elements.forEach((el) => {
                if (el.events) {
                  el.events.forEach((evt) => {
                    if (evt.onionRpcEvent.shortName === pendingWaitForEvent.expectedEventShortName) {
                      foundRequestedEvent = true;
                    }
                  });
                }
              });
              if (foundRequestedEvent) {
                pendingWaitForEvent.resolve(message);
                this.pendingWaitForEvent[i] = null;
              }
            }
            this.pendingWaitForEvent = this.pendingWaitForEvent.filter((x) => !!x);
          }
        } catch(e) {
          global.console.log(e);
          global.console.log(message);
        }
      } else {
        message.resultHtmlString = `<ul><li><b>Fehler</b>: ${message.error}</li></ul>`;
      }

      // console.log('processing message done', message);

      this.emit('message', message);

      if (this.waitForMessageHelper != null) {
        if (this.waitForMessageHelper.method === message.activeRequest.method) {
          this.waitForMessageHelper.resolve(message);
          this.waitForMessageHelper = null;
        }
      }
    }

  }

  onOnionRpcUnexpectedMessage(data) {
    let resultAsString = this.convertPayloadToString(data.result);

    const message = {
      timestamp: Date.now(),
      expected: false,
      resultAsString,
      ...data,
    };
    this.emit('message', message);
  }

  stopTimeout() {
    if (this.startupTimeout != null) {
      clearTimeout(this.startupTimeout);
      this.startupTimeout = null;
    }
  }

  start() {
    this.bootupStartTime = Date.now();
    this.emit('updateOutputValue', 1, 2, 0); // set bootup gpio to 0
    this.stopTimeout();
    this.onionBootupTime = this.options.onionBootupTime;
    this.startupTimeout = setTimeout(() => {
      this.bootupStartTime = null;
      this.channel.start();
      this.emit('updateOutputValue', 1, 2, 1); // set bootup gpio to 1
    }, this.options.onionBootupTime);
  }

  sendUartData(index, rxBuffer) {
    if (index === 0) {
      this.channel.receiveData(rxBuffer);
    }
  }

  setInputValueForType(index, type, value) {
    if (index === 0) { // controller to onion event pin
      if (type === 2) {
        this.channel.setEventGpioState(value);
      }
    } else if (index === 1) {
      // nop
    } else {
      const consumer = this.consumers.find(x => x.output && x.output.index === index);
      if (consumer) {
        consumer.type = type;
        consumer.value = value;
      }
    }
    // console.log(index, type, value);
  }

  intervalHandler() {
    const time = (this.onionBootupTime - (Date.now() - this.bootupStartTime)) / 1000;
    this.bootupTimeLeft = time > 0 ? time : 0;
  }

  performOnionRestart(reason, onionRestartTime) {
    this.stopTimeout();
    const resetHandling = () => {
      this.bootupStartTime = Date.now();
      this.stopTimeout();
      this.channel.stop(true);
      this.onionBootupTime = onionRestartTime;
      this.startupTimeout = setTimeout(() => {
        this.bootupStartTime = null;
        this.emit('updateOutputValue', 1, 2, 1); // set bootup gpio to 1
        this.channel.start();
      }, onionRestartTime);
    };

    this.emit('updateOutputValue', 1, 2, 0); // set bootup gpio to 0
    if (reason != null) {
      this.channel.sendRpcMessage(onionRpcGetMethodById('ReportReset').value, [reason]);
      this.startupTimeout = setTimeout(resetHandling.bind(this), reason === 40 ? 1000 : 1000); // TODO(robin): welche Zeit wartet der onion ? 5s?
    } else {
      resetHandling();
    }
  }

  async simulateFirmwareUpdate(performCombiUpdate, onionRestartTime) {
    if (this.waitForMessageHelper != null) {
      global.console.log('Cannot start Simulate Firmware Update, another action is in progress');
      return;
    }
    // NOTE(robin): Transmit length 512 only works, if the `BasicPlatform.c` uart rx buffer is large enough
    // This buffer acts as an temporary storage with the same size as the application tx buffer for this uart
    // If a guru meditation occures you have two ways to solve:
    //    1) Change `transimitByteLength` to 64 (or what fits in your application tx buffer)
    //       With this the simulation behaves in an other way than the real product
    //    2) Modify Applications Tx Buffer for example with this code
    //         #ifdef WASI
    //         static RVM_BUFFER_DECLARATION(__Uart4TxBufferBuffer, 1024); // Omega
    //         #else
    //         static RVM_BUFFER_DECLARATION(__Uart4TxBufferBuffer, 128); // Omega
    //         #endif
    //
    // The guru meditation looks like
    //
    //      ======== GURU MEDITATION ========
    //      BasicPlatformWasiBridge.js:66 File: .../ResolVM2/src/Platform/BasicPlatform.c
    //      BasicPlatformWasiBridge.js:66 Line: 2311
    //      BasicPlatformWasiBridge.js:66 Func: __VTable_Uart_Receive
    //      Message:
    //      BasicPlatformWasiBridge.js:66 Assertion failed: Rvm_Buffer_GetSpace(Entity->RxBuffer) >= Length
    const {
      storageIndex,
      transimitByteLength,
      firmwareFileSize,
      pageSize,
    } = this.options.firmwareUpdateSimulation;
    const pageBoundryMask = 0xFFFFF000;
    const bootHeaderOffset = (firmwareFileSize + pageSize - 1) & pageBoundryMask;


    const firmwareFileBuffer = Buffer.alloc(transimitByteLength);
    for (let i = 0; i < transimitByteLength; i += 2) {
      firmwareFileBuffer.writeUInt16LE(0xabdf, i);
    }

    const methodSetUpdateHeaderStartOffset = onionRpcGetMethodById('SetUpdateHeaderStartOffset');
    const methodInvalidateStorage = onionRpcGetMethodById('InvalidateStorage');
    const methodWriteStorage = onionRpcGetMethodById('WriteStorage');
    const methodTransmitOnionParameters = onionRpcGetMethodById('TransmitOnionParameters');

    // console.log(Date.now(), 'simulateFirmwareUpdate', 'send onion parameter');
    await this.channel.sendRpcMessageAndWaitForResponse(
      methodTransmitOnionParameters.value, methodTransmitOnionParameters.parameter.map((x => x.example)),
    );

    // console.log(Date.now(), 'simulateFirmwareUpdate', 'send startOffset 0');
    await this.channel.sendRpcMessageAndWaitForResponse(methodSetUpdateHeaderStartOffset.value, [0]);

    // console.log(Date.now(), 'simulateFirmwareUpdate', 'invalidate sorage');
    let nextOffset = 0;
    while (nextOffset < (bootHeaderOffset + pageSize)) {
      const response = await this.channel.sendRpcMessageAndWaitForResponse(methodInvalidateStorage.value, [storageIndex, nextOffset]);
      if (response && Array.isArray(response) && response.length > 1 && Array.isArray(response[1])) {
        // received expected valid answer
        nextOffset = response[1][0];
      }
    }

    // console.log(Date.now(), 'simulateFirmwareUpdate', 'write sorage');
    // Transmit firmware file except first 4k block
    nextOffset = pageSize;
    while (nextOffset < firmwareFileSize) {
      const length = Math.min(firmwareFileSize - nextOffset, transimitByteLength);
      await this.channel.sendRpcMessageAndWaitForResponse(methodWriteStorage.value, [ 0, nextOffset, Buffer.from(firmwareFileBuffer.slice(storageIndex, length)) ]);
      nextOffset += length;
    }

    // console.log(Date.now(), 'simulateFirmwareUpdate', 'send padding');
    // If firmware doesn't end on 4k page, add padding padding
    if (nextOffset % pageSize !== 0) {
      const paddingLength = pageSize - (nextOffset % pageSize) + nextOffset;
      const paddingBuffer = Buffer.alloc(transimitByteLength);
      while (nextOffset < paddingLength) {
        const length = Math.min(paddingLength - nextOffset, transimitByteLength);
        await this.channel.sendRpcMessageAndWaitForResponse(methodWriteStorage.value, [ 0, nextOffset, Buffer.from(paddingBuffer.slice(storageIndex, length)) ]);
        nextOffset += length;
      }
    }

    // console.log(Date.now(), 'simulateFirmwareUpdate', 'send first 4k block');
    // Transimit first 4k block to next memory page
    nextOffset = bootHeaderOffset;
    while (nextOffset < (bootHeaderOffset + pageSize)) {
      const length = Math.min(bootHeaderOffset + pageSize - nextOffset, transimitByteLength);
      await this.channel.sendRpcMessageAndWaitForResponse(methodWriteStorage.value, [ 0, nextOffset, Buffer.from(firmwareFileBuffer.slice(storageIndex, length)) ]);
      nextOffset += length;
    }

    // console.log(Date.now(), 'simulateFirmwareUpdate', `send startOffset 0x${bootHeaderOffset.toString(16)}`);
    await this.channel.sendRpcMessageAndWaitForResponse(methodSetUpdateHeaderStartOffset.value, [bootHeaderOffset]);
    // now the controller shows the update menu. You can start the update on the controller or via PerformUpdate RPC-Method

    if (performCombiUpdate) {
      const methodGetRpcVersion = onionRpcGetMethodById('GetRpcVersion');
      // console.log(Date.now(), 'simulateFirmwareUpdate', 'PerformConbiupdate');
      // console.log(Date.now(), 'simulateFirmwareUpdate', 'Wait for controller to start his update');
      await new Promise((resolve, reject) => {
        this.pendingWaitForEvent.push({
          resolve,
          reject,
          expectedEventShortName: 'START_UPDATE_NOTIFICATION',
        });
      });

      // console.log(Date.now(), 'simulateFirmwareUpdate', 'Wait for controller to reboot');
      await new Promise((resolve, reject) => {
        this.pendingWaitForEvent.push({
          resolve,
          reject,
          expectedEventShortName: 'BOOTUP_NOTIFICATION',
        });
      });

      // console.log(Date.now(), 'simulateFirmwareUpdate', 'Request Rpc Version');
      await this.channel.sendRpcMessageAndWaitForResponse(methodGetRpcVersion.value, []);

      // console.log(Date.now(), 'simulateFirmwareUpdate', 'SendOnionParameter');
      await this.channel.sendRpcMessageAndWaitForResponse(
        methodTransmitOnionParameters.value, methodTransmitOnionParameters.parameter.map((x => x.example)),
      );

      // console.log(Date.now(), 'simulateFirmwareUpdate', 'perform onion update');
      this.performOnionRestart(40, onionRestartTime);
    }

    // console.log(Date.now(), 'simulateFirmwareUpdate', `Update ${performCombiUpdate ? 'with' : 'without'} onion done`);
  }
}
